.root {
  @mixin light {
    --tab-border-color: var(--mantine-color-gray-3);
  }

  @mixin dark {
    --tab-border-color: var(--mantine-color-dark-4);
  }

  display: var(--tabs-display);
  flex-direction: var(--tabs-flex-direction);

  --tabs-list-direction: row;
  --tabs-panel-grow: unset;
  --tabs-display: block;
  --tabs-flex-direction: row;
  --tabs-list-border-width: 0;
  --tabs-list-border-size: 0 0 var(--tabs-list-border-width) 0;
  --tabs-list-gap: unset;

  --tabs-list-line-bottom: 0;
  --tabs-list-line-top: unset;
  --tabs-list-line-start: 0;
  --tabs-list-line-end: 0;

  --tab-radius: var(--tabs-radius) var(--tabs-radius) 0 0;
  --tab-border-width: 0 0 var(--tabs-list-border-width) 0;

  &[data-inverted] {
    --tabs-list-line-bottom: unset;
    --tabs-list-line-top: 0;
    --tab-radius: 0 0 var(--tabs-radius) var(--tabs-radius);
    --tab-border-width: var(--tabs-list-border-width) 0 0 0;

    & .list--default::before {
      top: 0;
      bottom: unset;
    }
  }

  &[data-orientation='vertical'] {
    --tabs-list-line-start: unset;
    --tabs-list-line-end: 0;
    --tabs-list-line-top: 0;
    --tabs-list-line-bottom: 0;
    --tabs-list-border-size: 0 var(--tabs-list-border-width) 0 0;
    --tab-border-width: 0 var(--tabs-list-border-width) 0 0;
    --tab-radius: var(--tabs-radius) 0 0 var(--tabs-radius);
    --tabs-list-direction: column;
    --tabs-panel-grow: 1;
    --tabs-display: flex;

    @mixin rtl {
      --tabs-list-border-size: 0 0 0 var(--tabs-list-border-width);
      --tab-border-width: 0 0 0 var(--tabs-list-border-width);
      --tab-radius: 0 var(--tabs-radius) var(--tabs-radius) 0;
    }

    &[data-placement='right'] {
      --tabs-flex-direction: row-reverse;
      --tabs-list-line-start: 0;
      --tabs-list-line-end: unset;
      --tabs-list-border-size: 0 0 0 var(--tabs-list-border-width);
      --tab-border-width: 0 0 0 var(--tabs-list-border-width);
      --tab-radius: 0 var(--tabs-radius) var(--tabs-radius) 0;

      @mixin rtl {
        --tabs-list-border-size: 0 var(--tabs-list-border-width) 0 0;
        --tab-border-width: 0 var(--tabs-list-border-width) 0 0;
        --tab-radius: var(--tabs-radius) 0 0 var(--tabs-radius);
      }
    }
  }

  &[data-variant='default'] {
    --tabs-list-border-width: 2px;

    @mixin light {
      --tab-hover-color: var(--mantine-color-gray-0);
    }

    @mixin dark {
      --tab-hover-color: var(--mantine-color-dark-6);
    }
  }

  &[data-variant='outline'] {
    --tabs-list-border-width: 1px;
  }

  &[data-variant='pills'] {
    --tabs-list-gap: calc(var(--mantine-spacing-sm) / 2);

    @mixin light {
      --tab-hover-color: var(--mantine-color-gray-0);
    }

    @mixin dark {
      --tab-hover-color: var(--mantine-color-dark-6);
    }
  }
}

.list {
  display: flex;
  flex-wrap: wrap;
  justify-content: var(--tabs-justify, flex-start);
  flex-direction: var(--tabs-list-direction);
  gap: var(--tabs-list-gap);

  &:where([data-grow]) .tab {
    flex: 1;
  }
}

.panel {
  flex-grow: var(--tabs-panel-grow);
}

.tab {
  position: relative;
  padding: var(--mantine-spacing-xs) var(--mantine-spacing-md);
  font-size: var(--mantine-font-size-sm);
  white-space: nowrap;
  z-index: 0;
  display: flex;
  align-items: center;
  line-height: 1;
  user-select: none;

  &:where(:disabled, [data-disabled]) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    z-index: 1;
  }
}

.tabSection {
  display: flex;
  align-items: center;
  justify-content: center;

  &:where([data-position='left']:not(:only-child)) {
    margin-inline-end: var(--mantine-spacing-xs);
  }

  &:where([data-position='right']:not(:only-child)) {
    margin-inline-start: var(--mantine-spacing-xs);
  }
}

.tabLabel {
  flex: 1;
  text-align: center;
}

/*************************************** default variant ***************************************/
.list--default {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    border: 1px solid var(--tab-border-color);
    bottom: var(--tabs-list-line-bottom);
    inset-inline-start: var(--tabs-list-line-start);
    inset-inline-end: var(--tabs-list-line-end);
    top: var(--tabs-list-line-top);
  }
}

.tab--default {
  border-radius: var(--tab-radius);
  border-width: var(--tab-border-width);
  border-style: solid;
  border-color: transparent;
  background-color: transparent;

  &:where([data-active]) {
    border-color: var(--tabs-color);
  }

  @mixin hover {
    background-color: var(--tab-hover-color);

    &:where(:not([data-active])) {
      border-color: var(--tab-border-color);
    }
  }

  &:disabled,
  &[data-disabled] {
    @mixin hover {
      background-color: transparent;
    }
  }
}

/*************************************** outline variant ***************************************/
.list--outline {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    border-color: var(--tab-border-color);
    border-width: var(--tabs-list-border-size);
    border-style: solid;
    bottom: var(--tabs-list-line-bottom);
    inset-inline-start: var(--tabs-list-line-start);
    inset-inline-end: var(--tabs-list-line-end);
    top: var(--tabs-list-line-top);
  }
}

.tab--outline {
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
  border-inline: 1px solid transparent;
  border-top-color: var(--tab-border-top-color);
  border-bottom-color: var(--tab-border-bottom-color);
  border-radius: var(--tab-radius);
  position: relative;

  --tab-border-bottom-color: transparent;
  --tab-border-top-color: transparent;
  --tab-border-inline-end-color: transparent;
  --tab-border-inline-start-color: transparent;

  &:where([data-active]) {
    &::before {
      content: '';
      position: absolute;
      background-color: var(--tab-border-color);
      bottom: var(--tab-before-bottom, rem(-1px));
      inset-inline-start: var(--tab-before-start, rem(-1px));
      inset-inline-end: var(--tab-before-end, auto);
      top: var(--tab-before-top, auto);
      width: 1px;
      height: 1px;
    }

    &::after {
      content: '';
      position: absolute;
      background-color: var(--tab-border-color);
      bottom: var(--tab-after-bottom, rem(-1px));
      inset-inline-end: var(--tab-after-end, rem(-1px));
      inset-inline-start: var(--tab-after-start, auto);
      top: var(--tab-after-top, auto);
      width: 1px;
      height: 1px;
    }

    border-top-color: var(--tab-border-top-color);
    border-bottom-color: var(--tab-border-bottom-color);
    border-inline-start-color: var(--tab-border-inline-start-color);
    border-inline-end-color: var(--tab-border-inline-end-color);

    --tab-border-top-color: var(--tab-border-color);
    --tab-border-inline-start-color: var(--tab-border-color);
    --tab-border-inline-end-color: var(--tab-border-color);
    --tab-border-bottom-color: var(--mantine-color-body);

    &[data-inverted] {
      --tab-border-bottom-color: var(--tab-border-color);
      --tab-border-top-color: var(--mantine-color-body);

      --tab-before-bottom: auto;
      --tab-before-top: -1px;
      --tab-after-bottom: auto;
      --tab-after-top: -1px;
    }

    &[data-orientation='vertical'] {
      &[data-placement='left'] {
        --tab-border-inline-end-color: var(--mantine-color-body);
        --tab-border-inline-start-color: var(--tab-border-color);
        --tab-border-bottom-color: var(--tab-border-color);

        --tab-before-end: -1px;
        --tab-before-start: auto;
        --tab-before-bottom: auto;
        --tab-before-top: -1px;
        --tab-after-start: auto;
        --tab-after-end: -1px;
      }

      &[data-placement='right'] {
        --tab-border-inline-start-color: var(--mantine-color-body);
        --tab-border-inline-end-color: var(--tab-border-color);
        --tab-border-bottom-color: var(--tab-border-color);

        --tab-before-start: -1px;
        --tab-before-end: auto;
        --tab-before-bottom: auto;
        --tab-before-top: -1px;
        --tab-after-end: auto;
        --tab-after-start: -1px;
      }
    }
  }
}

/*************************************** pills variant ***************************************/
.tab--pills {
  border-radius: var(--tabs-radius);
  background-color: var(--tab-bg);
  color: var(--tab-color);

  --tab-bg: transparent;
  --tab-color: inherit;

  &:not([data-disabled]) {
    @mixin hover {
      --tab-bg: var(--tab-hover-color);
    }
  }

  &[data-active][data-active] {
    --tab-bg: var(--tabs-color);
    --tab-color: var(--tabs-text-color, var(--mantine-color-white));

    @mixin hover {
      --tab-bg: var(--tabs-color);
    }
  }
}
